import React, { useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getOrders, deleteOrder } from '../../services/api';
import { useApiQuery, useApiMutation, useInvalidateQueries } from '../../hooks/useApiQuery';
import { useSocket } from '../../context/SocketContext';
import useToast from '../../hooks/useToast';
import { format } from 'date-fns';
import { PlusIcon } from '@heroicons/react/24/outline';

const VendorOrders = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const socket = useSocket();
  const invalidate = useInvalidateQueries();

  // ✅ React Query: Fetch orders
  const {
    data: orders = [],
    isLoading,
    refetch: refetchOrders,
  } = useApiQuery(
    ['vendor-orders'],
    () => getOrders().then(res => res.data),
    {
      staleTime: 10 * 1000, // ✅ Reduced to 10 seconds for faster updates
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      onError: () => {
        showError('Failed to fetch orders');
      },
    }
  );

  // ✅ React Query: Delete order mutation
  const deleteOrderMutation = useApiMutation(
    (orderId) => deleteOrder(orderId),
    {
      onSuccess: () => {
        showSuccess('Order cancelled successfully');
        // ✅ Invalidate and refetch
        invalidate(['vendor-orders']);
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to cancel order');
      },
    }
  );

  // ✅ Socket listener for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleNewOrder = () => {
      console.log('🔄 New order created, refreshing...');
      invalidate(['vendor-orders']);
    };

    const handleOrderPaid = () => {
      console.log('💰 Order paid, refreshing...');
      invalidate(['vendor-orders']);
    };

    socket.on('orderCreated', handleNewOrder);
    socket.on('orderPaid', handleOrderPaid);

    return () => {
      socket.off('orderCreated', handleNewOrder);
      socket.off('orderPaid', handleOrderPaid);
    };
  }, [socket, invalidate]);

  const handlePay = useCallback((orderId) => {
    navigate(`/vendor/payment/${orderId}`);
  }, [navigate]);

  // ✅ Handle Cancel/Delete Order
  const handleCancelOrder = useCallback((orderId) => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    deleteOrderMutation.mutate(orderId);
  }, [deleteOrderMutation]);

  if (isLoading) {
    return <div className="text-center py-10 text-[#708090]">Loading orders...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-normal text-[#2b3445]">Orders</h1>
        <Link
          to="/vendor/orders/create"
          className="bg-[#1471b9] text-white px-4 py-2 rounded-[8px] text-sm font-bold hover:opacity-90 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          New Order
        </Link>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-8 text-[#708090]">No orders yet.</div>
      ) : (
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] overflow-hidden">
          <table className="min-w-full divide-y divide-[#dae1e7]">
            <thead className="bg-[#f8f9fa]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Order ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Attendee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Items</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#708090] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#dae1e7]">
              {orders.map((order) => (
                <tr key={order._id} className="hover:bg-[#f8f9fa]">
                  <td className="px-4 py-3 text-sm text-[#2b3445] font-mono">{order._id.slice(-6)}</td>
                  <td className="px-4 py-3 text-sm text-[#2b3445]">{order.attendeeId?.name || order.attendeeId}</td>
                  <td className="px-4 py-3 text-sm text-[#708090]">{order.items.length}</td>
                  <td className="px-4 py-3 text-sm font-semibold">₹{order.total.toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      order.status === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#708090]">
                    {format(new Date(order.createdAt), 'dd MMM yyyy')}
                  </td>
                  
                  {/* Actions Column */}
                  <td className="px-4 py-3 text-right space-x-2">
                    {order.status === 'pending' ? (
                      <>
                        <button
                          onClick={() => handlePay(order._id)}
                          className="bg-green-500 text-white px-3 py-1 rounded text-xs hover:bg-green-600 transition-colors"
                        >
                          Pay
                        </button>
                        <Link
                          to={`/vendor/orders/qr/${order._id}`}
                          className="bg-[#1471b9] text-white px-3 py-1 rounded text-xs hover:opacity-90 transition-opacity"
                        >
                          QR
                        </Link>
                        {/* Cancel Button */}
                        <button
                          onClick={() => handleCancelOrder(order._id)}
                          disabled={deleteOrderMutation.isPending}
                          className="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 transition-colors disabled:opacity-50"
                        >
                          {deleteOrderMutation.isPending ? '...' : 'Cancel'}
                        </button>
                      </>
                    ) : (
                      <Link
                        to={`/vendor/orders/${order._id}/bill`}
                        className="bg-[#1471b9] text-white px-3 py-1 rounded text-xs hover:opacity-90 transition-opacity"
                      >
                        View Bill
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VendorOrders;